import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  random,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// PALET BAKU (HARD CONSTRAINT)
const BG = "#111815";
const CARD = "#18231f";
const BORDER = "#283a33";
const WHITE = "#f4f6f4";
const LIME = "#e8f36e";
const RED = "#e04336";
const WA_GREEN = "#25D366";

// FONT DECLARATIONS VIA STATICFILE
const fontFaces = `
@font-face {
  font-family: 'TekoDisplay';
  src: url('${staticFile("fonts/Teko-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'BebasDisplay';
  src: url('${staticFile("fonts/BebasNeue-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'AntonDisplay';
  src: url('${staticFile("fonts/Anton-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
`;

export const KyuStoreSatirePro: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Helper spring generator
  const getSceneSpring = (startFrame: number, damping = 15, stiffness = 160) =>
    spring({
      frame: Math.max(0, frame - startFrame),
      fps,
      config: { damping, stiffness },
    });

  // Deterministic micro offsets using random("seed")
  const seedShiftX = (random(`shift-x-${Math.floor(frame / 6)}`) - 0.5) * 4;
  const seedShiftY = (random(`shift-y-${Math.floor(frame / 6)}`) - 0.5) * 4;

  // Scene Booleans (13 SCENES)
  const isS1 = frame < 90;
  const isS2 = frame >= 90 && frame < 180;
  const isS3 = frame >= 180 && frame < 330;
  const isS4 = frame >= 330 && frame < 480;
  const isS5 = frame >= 480 && frame < 630;
  const isS6 = frame >= 630 && frame < 780;
  const isS7 = frame >= 780 && frame < 960;
  const isS8 = frame >= 960 && frame < 1100;
  const isS9 = frame >= 1100 && frame < 1260;
  const isS10 = frame >= 1260 && frame < 1440;
  const isS11 = frame >= 1440 && frame < 1620;
  const isS12 = frame >= 1620 && frame < 1800;
  const isS13 = frame >= 1800;

  // SUB JUDUL DINAMIS
  const getSubtitle = (f: number): string => {
    if (f < 90) return "Gue heran deh... Mau beli diamond dua puluh ribu aja...";
    if (f < 180) return "Kenapa toko sebelah harus minta nomor WhatsApp sama email sih?!";
    if (f < 330) return "Lu mau jajan game apa mau daftar sensus penduduk?";
    if (f < 480) return "Udah minta nomor WA, ujung-ujungnya nomor kita dispam promo tiap subuh.";
    if (f < 630) return "Jam 3 pagi dapet chat promo gak jelas... ngeganggu tidur banget.";
    if (f < 780) return "Email juga isinya notifikasi numpuk. Padahal niat kita simpel: mau beli pass, kelar.";
    if (f < 960) return "Lagi di lobby mabar disuruh ketik email berkali-kali... masa main game pake birokrasi?";
    if (f < 1100) return "Makanya sekarang gue pindah total ke Kyu Store. Di sini beneran gak ada drama!";
    if (f < 1260) return "Gak ada minta-minta nomor WhatsApp. Gak ada palak email!";
    if (f < 1440) return "Lu cuma buka web, masukin ID game lu—simpel gak pake basa-basi.";
    if (f < 1620) return "Detik itu juga nickname game lu langsung kedeteksi secara otomatis!";
    if (f < 1800) return "Pilih nominal, scan QRIS, beres. Satu detik langsung mendarat di akun game lu!";
    return "Top up anti-ribet? Langsung buka kyustore.com sekarang!";
  };

  const currentSubtitle = getSubtitle(frame);

  // Background color condition for S8 Hard Reset
  const activeBg = isS8 ? LIME : BG;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: activeBg,
        color: isS8 ? BG : WHITE,
        fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{fontFaces}</style>

      {/* AUDIO VO SATIRE */}
      <Audio src={staticFile("audio/satire_mastered.mp3")} volume={1} />

      {/* TOP HEADER STATUS BAR (GLOBAL OVERLAY) */}
      {!isS8 && (
        <div
          style={{
            position: "absolute",
            top: 100,
            left: 90,
            right: 90,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `2px solid ${BORDER}`,
            paddingBottom: 20,
            zIndex: 90,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 14,
                height: 14,
                backgroundColor: isS1 || isS2 || isS3 || isS4 || isS5 || isS6 || isS7 ? RED : LIME,
              }}
            />
            <span
              style={{
                fontFamily: "'BebasDisplay', 'Bebas Neue', sans-serif",
                fontSize: 26,
                letterSpacing: "0.14em",
                color: WHITE,
              }}
            >
              KYU STORE // SATIRE REALITA
            </span>
          </div>

          <div
            style={{
              padding: "6px 14px",
              backgroundColor: CARD,
              border: `1px solid ${BORDER}`,
              fontFamily: "monospace",
              fontSize: 16,
              color: isS1 || isS2 || isS3 || isS4 || isS5 || isS6 || isS7 ? RED : LIME,
              fontWeight: "bold",
              letterSpacing: "0.08em",
            }}
          >
            {isS1 && "REALITA GAMER 01/13"}
            {isS2 && "KRITIK SISTEM 02/13"}
            {isS3 && "BIROKRASI KONYOL 03/13"}
            {isS4 && "EXPOSED DATA 04/13"}
            {isS5 && "SPAM SUBUH 05/13"}
            {isS6 && "EMAIL SAMPAH 06/13"}
            {isS7 && "PANIK LOBBY 07/13"}
            {isS9 && "SOLUSI KYU 09/13"}
            {isS10 && "STEP 1 CUMA ID 10/13"}
            {isS11 && "AUTO DETECT 11/13"}
            {isS12 && "INSTANT SETTLE 12/13"}
            {isS13 && "OUTRO RESMI 13/13"}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 1 [0 - 90]: "MAU BELI DIAMOND 20RB..." — FULL-BLEED BIG TYPE       */}
      {/* ========================================================================= */}
      {isS1 && (() => {
        const spr = getSceneSpring(0);
        const yOffset = interpolate(spr, [0, 1], [80, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const numScale = interpolate(frame, [0, 90], [1, 1.08], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transform: `translateY(${yOffset}px)`,
            }}
          >
            {/* CORNER TAG */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  backgroundColor: RED,
                  color: WHITE,
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 28,
                  letterSpacing: "0.1em",
                }}
              >
                REALITA GAMER 01/13
              </div>
            </div>

            {/* FULL BLEED MASSIVE TYPOGRAPHY */}
            <div>
              <div
                style={{
                  fontFamily: "'TekoDisplay', sans-serif",
                  fontSize: 100,
                  lineHeight: 0.9,
                  color: WHITE,
                  letterSpacing: "0.02em",
                }}
              >
                MAU BELI
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 140,
                  lineHeight: 0.9,
                  color: LIME,
                  letterSpacing: "-0.02em",
                  marginTop: 10,
                }}
              >
                DIAMOND
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 20,
                  marginTop: 14,
                  transform: `scale(${numScale})`,
                  transformOrigin: "left center",
                }}
              >
                <span
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 190,
                    lineHeight: 0.85,
                    color: WHITE,
                  }}
                >
                  20RB
                </span>
                <span
                  style={{
                    fontFamily: "'BebasDisplay', sans-serif",
                    fontSize: 44,
                    color: RED,
                    backgroundColor: CARD,
                    padding: "8px 16px",
                    border: `2px solid ${RED}`,
                  }}
                >
                  CUMA 20 RIBU PERAK!
                </span>
              </div>
            </div>

            {/* ASYMMETRIC STAT BOX */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "28px 32px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 18, color: "#8a9e94", fontFamily: "monospace" }}>
                  TRANSAKSI NOMINAL KECIL
                </div>
                <div
                  style={{
                    fontFamily: "'BebasDisplay', sans-serif",
                    fontSize: 40,
                    color: WHITE,
                    marginTop: 4,
                  }}
                >
                  TAPI BIROKRASI SELANGIT
                </div>
              </div>
              <div
                style={{
                  width: 56,
                  height: 56,
                  backgroundColor: RED,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: WHITE,
                  fontSize: 32,
                  fontWeight: 900,
                }}
              >
                !
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 2 [90 - 180]: "KENAPA MINTA NO WA & EMAIL?!" — RED BLOCK & STRIKE    */}
      {/* ========================================================================= */}
      {isS2 && (() => {
        const spr = getSceneSpring(90);
        const scale = interpolate(spr, [0, 1], [0.85, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const strikeWidth = interpolate(frame, [110, 150], [0, 100], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 230,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 24,
              transform: `scale(${scale})`,
            }}
          >
            {/* THICK RED HEADER BLOCK */}
            <div
              style={{
                backgroundColor: RED,
                padding: "36px 32px",
                border: `3px solid ${WHITE}`,
              }}
            >
              <div
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 32,
                  letterSpacing: "0.1em",
                  color: WHITE,
                }}
              >
                PERTANYAAN LOGIKA DASAR:
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 90,
                  lineHeight: 0.95,
                  color: WHITE,
                  marginTop: 10,
                }}
              >
                KENAPA MINTA DATA PRIBADI?!
              </div>
            </div>

            {/* STRIKE THROUGH SECTION */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "36px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              {/* STRIKE ITEM 1: NO WA */}
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    fontFamily: "'TekoDisplay', sans-serif",
                    fontSize: 84,
                    lineHeight: 0.9,
                    color: "#6b7d74",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>1. NOMOR WHATSAPP</span>
                  <span style={{ color: RED }}>✕ DITOLAK</span>
                </div>
                {/* STRIKE BAR */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    width: `${strikeWidth}%`,
                    height: 10,
                    backgroundColor: RED,
                    transform: "translateY(-50%)",
                  }}
                />
              </div>

              {/* STRIKE ITEM 2: EMAIL */}
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    fontFamily: "'TekoDisplay', sans-serif",
                    fontSize: 84,
                    lineHeight: 0.9,
                    color: "#6b7d74",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>2. ALAMAT EMAIL</span>
                  <span style={{ color: RED }}>✕ DITOLAK</span>
                </div>
                {/* STRIKE BAR */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    width: `${strikeWidth}%`,
                    height: 10,
                    backgroundColor: RED,
                    transform: "translateY(-50%)",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px",
                backgroundColor: CARD,
                border: `1px solid ${RED}`,
              }}
            >
              <span style={{ fontFamily: "monospace", fontSize: 20, color: RED }}>
                [WARNING: RAWAN DIBAGIKAN KE PIHAK KETIGA]
              </span>
              <span style={{ fontFamily: "'BebasDisplay'", fontSize: 24, color: WHITE }}>
                BAHAYA SPAM
              </span>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 3 [180 - 330]: "MAU JAJAN GAME APA SENSUS PENDUDUK?" — SPLIT 50:50   */}
      {/* ========================================================================= */}
      {isS3 && (() => {
        const spr = getSceneSpring(180);
        const splitY = interpolate(spr, [0, 1], [60, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 20,
              transform: `translateY(${splitY}px)`,
            }}
          >
            {/* TOP HALF: JAJAN GAME (KECIL) */}
            <div
              style={{
                flex: 1,
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "24px 30px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  style={{
                    fontFamily: "'BebasDisplay', sans-serif",
                    fontSize: 26,
                    color: LIME,
                    letterSpacing: "0.08em",
                  }}
                >
                  OPSI A: TUJUAN AWAL
                </span>
                <span
                  style={{
                    backgroundColor: LIME,
                    color: BG,
                    fontWeight: "bold",
                    fontSize: 16,
                    padding: "4px 10px",
                  }}
                >
                  SIMPEL
                </span>
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 78,
                  lineHeight: 0.9,
                  color: WHITE,
                }}
              >
                JAJAN GAME
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 16,
                  borderTop: `1px solid ${BORDER}`,
                  paddingTop: 12,
                }}
              >
                <span
                  style={{
                    fontFamily: "'TekoDisplay', sans-serif",
                    fontSize: 64,
                    color: LIME,
                    lineHeight: 0.8,
                  }}
                >
                  1 DATA
                </span>
                <span style={{ fontSize: 20, color: "#8a9e94" }}>HANYA BUTUH USER ID GAME</span>
              </div>
            </div>

            {/* CENTER VS BADGE */}
            <div
              style={{
                alignSelf: "center",
                width: 70,
                height: 40,
                backgroundColor: RED,
                color: WHITE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "'AntonDisplay', sans-serif",
                fontSize: 26,
                border: `2px solid ${WHITE}`,
                zIndex: 10,
                marginTop: -10,
                marginBottom: -10,
              }}
            >
              VS
            </div>

            {/* BOTTOM HALF: SENSUS PENDUDUK (ABSURD) */}
            <div
              style={{
                flex: 1,
                backgroundColor: CARD,
                border: `2px solid ${RED}`,
                padding: "24px 30px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  style={{
                    fontFamily: "'BebasDisplay', sans-serif",
                    fontSize: 26,
                    color: RED,
                    letterSpacing: "0.08em",
                  }}
                >
                  OPSI B: TOKO SEBELAH
                </span>
                <span
                  style={{
                    backgroundColor: RED,
                    color: WHITE,
                    fontWeight: "bold",
                    fontSize: 16,
                    padding: "4px 10px",
                  }}
                >
                  ABSURD
                </span>
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 78,
                  lineHeight: 0.9,
                  color: RED,
                }}
              >
                SENSUS PENDUDUK?
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 16,
                  borderTop: `1px solid ${BORDER}`,
                  paddingTop: 12,
                }}
              >
                <span
                  style={{
                    fontFamily: "'TekoDisplay', sans-serif",
                    fontSize: 64,
                    color: RED,
                    lineHeight: 0.8,
                  }}
                >
                  16+ FORM
                </span>
                <span style={{ fontSize: 20, color: "#8a9e94" }}>MINTA WA, EMAIL, OTP, AKUN</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 4 [330 - 480]: FORM NO WA "[DATA BOCOR]" FIELD MERAH + "TAHAP 1"    */}
      {/* ========================================================================= */}
      {isS4 && (() => {
        const spr = getSceneSpring(330);
        const blink = Math.floor(frame / 12) % 2 === 0;

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 22,
            }}
          >
            {/* TAHAP 1 BANNER */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "16px 24px",
              }}
            >
              <span
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 32,
                  color: LIME,
                  letterSpacing: "0.1em",
                }}
              >
                ALUR FORMULIR TOKO SEBELAH
              </span>
              <span
                style={{
                  backgroundColor: RED,
                  color: WHITE,
                  padding: "6px 14px",
                  fontWeight: 900,
                  fontSize: 18,
                  fontFamily: "monospace",
                }}
              >
                TAHAP 1
              </span>
            </div>

            {/* MOCKUP FORM INPUT FIELD */}
            <div
              style={{
                backgroundColor: CARD,
                border: `3px solid ${RED}`,
                padding: "36px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <label
                style={{
                  fontSize: 20,
                  color: "#8a9e94",
                  fontFamily: "monospace",
                  letterSpacing: "0.06em",
                }}
              >
                INPUT NOMOR WHATSAPP AKTIF (WAJIB) *
              </label>

              {/* INPUT BOX MERAH */}
              <div
                style={{
                  backgroundColor: BG,
                  border: `2px solid ${RED}`,
                  padding: "20px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: 34,
                    color: WHITE,
                    letterSpacing: "0.06em",
                  }}
                >
                  +62 812-9843-XXXX
                </span>
                <span
                  style={{
                    width: 14,
                    height: 36,
                    backgroundColor: blink ? RED : "transparent",
                  }}
                />
              </div>

              {/* STAMP DATA BOCOR */}
              <div
                style={{
                  marginTop: 10,
                  padding: "14px 20px",
                  backgroundColor: RED,
                  color: WHITE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 32,
                    letterSpacing: "0.05em",
                  }}
                >
                  [DATA BOCOR]
                </span>
                <span style={{ fontFamily: "monospace", fontSize: 18, fontWeight: "bold" }}>
                  RISIKO SPAM 100%
                </span>
              </div>
            </div>

            {/* REASON LIST */}
            <div
              style={{
                backgroundColor: CARD,
                border: `1px solid ${BORDER}`,
                padding: "24px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <span style={{ color: RED, fontWeight: "bold", fontSize: 24 }}>✕</span>
                <span style={{ fontSize: 20, color: WHITE }}>Nomor masuk ke database marketing massal</span>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <span style={{ color: RED, fontWeight: "bold", fontSize: 24 }}>✕</span>
                <span style={{ fontSize: 20, color: WHITE }}>Siap-siap dapet spam promo pinjol & judi online</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 5 [480 - 630]: CHAT WA SPAM SUBUH 03:41 AM (BUBBLE HIJAU)           */}
      {/* ========================================================================= */}
      {isS5 && (() => {
        const spr = getSceneSpring(480);
        const bubbleY1 = interpolate(getSceneSpring(490), [0, 1], [40, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const bubbleY2 = interpolate(getSceneSpring(520), [0, 1], [40, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            {/* CHAT HEADER MOCKUP */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    backgroundColor: WA_GREEN,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontFamily: "'BebasDisplay', sans-serif",
                      fontSize: 28,
                      color: WHITE,
                      letterSpacing: "0.06em",
                    }}
                  >
                    WHATSAPP SPAM INCOMING
                  </div>
                  <div style={{ fontSize: 16, color: RED, fontFamily: "monospace" }}>
                    WAKTU: 03:41 AM (TENGAH MALEM)
                  </div>
                </div>
              </div>

              {/* TAG SPAM DETECTED */}
              <div
                style={{
                  backgroundColor: RED,
                  color: WHITE,
                  padding: "8px 16px",
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 24,
                  letterSpacing: "0.06em",
                }}
              >
                SPAM DETECTED
              </div>
            </div>

            {/* CHAT BUBBLES CONTAINER */}
            <div
              style={{
                backgroundColor: BG,
                border: `2px solid ${BORDER}`,
                padding: "28px 24px",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {/* BUBBLE 1 */}
              <div
                style={{
                  alignSelf: "flex-start",
                  maxWidth: "85%",
                  backgroundColor: WA_GREEN,
                  color: "#0a1f13",
                  padding: "20px 24px",
                  borderLeft: `6px solid ${RED}`,
                  transform: `translateY(${bubbleY1}px)`,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: "bold", opacity: 0.8, marginBottom: 6 }}>
                  BROADCAST BOT #941 • 03:41 AM
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
                  "HALO BOSSKU! ADA PROMO SCATTER HITAM & BONUS 100%! KLIK LINK DI BIO SEKARANG..."
                </div>
              </div>

              {/* BUBBLE 2 */}
              <div
                style={{
                  alignSelf: "flex-start",
                  maxWidth: "85%",
                  backgroundColor: WA_GREEN,
                  color: "#0a1f13",
                  padding: "20px 24px",
                  borderLeft: `6px solid ${RED}`,
                  transform: `translateY(${bubbleY2}px)`,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, opacity: 0.8, marginBottom: 6 }}>
                  MARKETING PROMO • 03:42 AM
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
                  "DANA TUNAI KILAT 5 MENIT CAIR TANPA SURVEY! BURUAN AJUKAN..."
                </div>
              </div>
            </div>

            {/* ASYMMETRIC OUTCOME STAMP */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${RED}`,
                padding: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 22, fontWeight: "bold", color: WHITE }}>
                Niat beli diamond 20rb... dapetnya gangguan tidur.
              </span>
              <span
                style={{
                  backgroundColor: RED,
                  color: WHITE,
                  padding: "4px 10px",
                  fontFamily: "monospace",
                  fontWeight: "bold",
                }}
              >
                TERBUKTI
              </span>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 6 [630 - 780]: INBOX EMAIL 99+ NEWSLETTER SAMPAH (LIST ITEM)        */}
      {/* ========================================================================= */}
      {isS6 && (() => {
        const spr = getSceneSpring(630);
        const yOffset = interpolate(spr, [0, 1], [60, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 20,
              transform: `translateY(${yOffset}px)`,
            }}
          >
            {/* INBOX HEADER */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "22px 28px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "'BebasDisplay', sans-serif",
                    fontSize: 34,
                    color: WHITE,
                    letterSpacing: "0.06em",
                  }}
                >
                  KOTAK MASUK EMAIL LU
                </div>
                <div style={{ fontSize: 16, color: "#8a9e94", fontFamily: "monospace" }}>
                  KORBAN REGISTRASI FORM TOKO LAIN
                </div>
              </div>

              <div
                style={{
                  backgroundColor: RED,
                  color: WHITE,
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 36,
                  padding: "6px 18px",
                  border: `2px solid ${WHITE}`,
                }}
              >
                99+ UNREAD
              </div>
            </div>

            {/* EMAIL LIST ITEMS */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                {
                  from: "TOKO SEBELAH PROMO",
                  time: "02:15 AM",
                  subject: "Jangan Lewatkan Diskon 1% Malam Ini!",
                },
                {
                  from: "NEWSLETTER HARIAN #412",
                  time: "KEMARIN",
                  subject: "Katalog Event Game yang Lu Gak Pernah Mainkan...",
                },
                {
                  from: "MARKETING PARTNER",
                  time: "2 HARI LALU",
                  subject: "Verifikasi Ulang Akun Anda Sekarang Juga!",
                },
              ].map((item, idx) => {
                const itemSpr = getSceneSpring(640 + idx * 25);
                const itemX = interpolate(itemSpr, [0, 1], [40, 0], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });

                return (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: CARD,
                      border: `1.5px solid ${BORDER}`,
                      borderLeft: `6px solid ${RED}`,
                      padding: "20px 24px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      transform: `translateX(${itemX}px)`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 16,
                        fontFamily: "monospace",
                        color: RED,
                      }}
                    >
                      <span>[SPAM // {item.from}]</span>
                      <span>{item.time}</span>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: WHITE }}>
                      {item.subject}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* SUMMARY BAR */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 22, color: WHITE }}>
                Padahal niat awal simpel: <strong style={{ color: LIME }}>mau beli pass, kelar.</strong>
              </span>
              <span
                style={{
                  fontFamily: "'BebasDisplay'",
                  fontSize: 24,
                  color: RED,
                }}
              >
                BIROKRASI SAMPAH
              </span>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 7 [780 - 960]: PANIK LOBBY + OTP ANTRIAN #47 (STAMP "PENDING")       */}
      {/* ========================================================================= */}
      {isS7 && (() => {
        const spr = getSceneSpring(780);
        const stampScale = interpolate(getSceneSpring(820), [0, 1], [2, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 22,
            }}
          >
            {/* LOBBY PANIC HEADER */}
            <div
              style={{
                backgroundColor: RED,
                padding: "24px 28px",
                border: `3px solid ${WHITE}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontFamily: "monospace", fontSize: 18, color: WHITE }}>
                  STATUS GAME: IN LOBBY MABAR
                </div>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 54,
                    color: WHITE,
                    lineHeight: 1,
                    marginTop: 6,
                  }}
                >
                  TEMEN UDAH GASIK READY!
                </div>
              </div>
              <div
                style={{
                  backgroundColor: WHITE,
                  color: RED,
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 32,
                  padding: "8px 16px",
                }}
              >
                MATCH: 00:15
              </div>
            </div>

            {/* QUEUE & OTP CARD WITH GIANT STAMP */}
            <div
              style={{
                position: "relative",
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "36px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              <div>
                <div style={{ fontSize: 18, color: "#8a9e94", fontFamily: "monospace" }}>
                  STATUS TRANSAKSI TOKO SEBELAH
                </div>
                <div
                  style={{
                    fontFamily: "'TekoDisplay', sans-serif",
                    fontSize: 100,
                    lineHeight: 0.85,
                    color: RED,
                    marginTop: 8,
                  }}
                >
                  ANTRIAN #47
                </div>
              </div>

              <div
                style={{
                  borderTop: `1px solid ${BORDER}`,
                  paddingTop: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 20, color: WHITE }}>VERIFIKASI OTP EMAIL:</span>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: 24,
                    color: RED,
                    fontWeight: "bold",
                  }}
                >
                  KIRIM ULANG (01:59)
                </span>
              </div>

              {/* ROTATED GIANT STAMP "PENDING" */}
              <div
                style={{
                  position: "absolute",
                  top: "30%",
                  right: "10%",
                  transform: `scale(${stampScale}) rotate(-14deg)`,
                  border: `6px solid ${RED}`,
                  color: RED,
                  padding: "12px 30px",
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 72,
                  letterSpacing: "0.1em",
                  backgroundColor: "rgba(17, 24, 21, 0.95)",
                }}
              >
                PENDING
              </div>
            </div>

            <div
              style={{
                backgroundColor: CARD,
                border: `1px solid ${BORDER}`,
                padding: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 22, color: WHITE }}>
                Masa main game aja disuruh ngelewatin birokrasi?
              </span>
              <span
                style={{
                  fontFamily: "'BebasDisplay'",
                  fontSize: 24,
                  color: RED,
                }}
              >
                KONYOL!
              </span>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 8 [960 - 1100]: HARD RESET "SKIP BIROKRASINYA. PINDAH KE KYU STORE." */}
      {/* ========================================================================= */}
      {isS8 && (() => {
        const spr = getSceneSpring(960, 12, 180);
        const textY = interpolate(spr, [0, 1], [100, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transform: `translateY(${textY}px)`,
            }}
          >
            {/* HARD RESET BADGE */}
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div
                style={{
                  backgroundColor: BG,
                  color: LIME,
                  padding: "12px 24px",
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 32,
                  letterSpacing: "0.1em",
                }}
              >
                HARD RESET // BERHENTI RIBET
              </div>
            </div>

            {/* GIANT STATEMENT IN BG COLOR */}
            <div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 104,
                  lineHeight: 0.92,
                  color: BG,
                  letterSpacing: "-0.01em",
                }}
              >
                SKIP
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 104,
                  lineHeight: 0.92,
                  color: BG,
                  letterSpacing: "-0.01em",
                }}
              >
                BIROKRASINYA.
              </div>
              <div
                style={{
                  marginTop: 30,
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 54,
                  color: BG,
                  letterSpacing: "0.08em",
                }}
              >
                PINDAH TOTAL KE
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 140,
                  lineHeight: 0.9,
                  color: BG,
                  borderBottom: `8px solid ${BG}`,
                  paddingBottom: 10,
                }}
              >
                KYU STORE.
              </div>
            </div>

            {/* PILLS */}
            <div
              style={{
                display: "flex",
                gap: 16,
              }}
            >
              <div
                style={{
                  flex: 1,
                  backgroundColor: BG,
                  color: WHITE,
                  padding: "16px 20px",
                  fontFamily: "monospace",
                  fontSize: 18,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                ✓ GAK ADA DRAMA
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: BG,
                  color: WHITE,
                  padding: "16px 20px",
                  fontFamily: "monospace",
                  fontSize: 18,
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                ✓ 100% INSTAN
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 9 [1100 - 1260]: MANIFESTO 2 KOLOM NO WA / NO EMAIL (TIDAK BUTUH)   */}
      {/* ========================================================================= */}
      {isS9 && (() => {
        const spr = getSceneSpring(1100);
        const cardY = interpolate(spr, [0, 1], [60, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 24,
              transform: `translateY(${cardY}px)`,
            }}
          >
            {/* MANIFESTO HEADER */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "20px 28px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 34,
                  color: WHITE,
                  letterSpacing: "0.08em",
                }}
              >
                MANIFESTO PRIVASI KYU STORE
              </span>
              <span
                style={{
                  backgroundColor: LIME,
                  color: BG,
                  fontFamily: "monospace",
                  fontWeight: "bold",
                  padding: "6px 14px",
                  fontSize: 16,
                }}
              >
                ZERO DATA MINING
              </span>
            </div>

            {/* 2-COLUMN SPLIT */}
            <div style={{ display: "flex", gap: 20, flex: 1 }}>
              {/* KOLOM 1: NO WA */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: CARD,
                  border: `3px solid ${BORDER}`,
                  padding: "32px 24px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: "'BebasDisplay', sans-serif",
                      fontSize: 32,
                      color: "#8a9e94",
                    }}
                  >
                    ATURAN #01
                  </div>
                  <div
                    style={{
                      fontFamily: "'AntonDisplay', sans-serif",
                      fontSize: 64,
                      lineHeight: 0.95,
                      color: WHITE,
                      marginTop: 8,
                    }}
                  >
                    NO WA!
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: RED,
                    color: WHITE,
                    padding: "12px 16px",
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 28,
                    textAlign: "center",
                    letterSpacing: "0.05em",
                  }}
                >
                  TIDAK BUTUH
                </div>

                <div style={{ fontSize: 18, color: "#8a9e94", lineHeight: 1.4 }}>
                  Nol nomor ponsel dicatat. Nol chat spam subuh. Lu aman 100%.
                </div>
              </div>

              {/* KOLOM 2: NO EMAIL */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: CARD,
                  border: `3px solid ${BORDER}`,
                  padding: "32px 24px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: "'BebasDisplay', sans-serif",
                      fontSize: 32,
                      color: "#8a9e94",
                    }}
                  >
                    ATURAN #02
                  </div>
                  <div
                    style={{
                      fontFamily: "'AntonDisplay', sans-serif",
                      fontSize: 64,
                      lineHeight: 0.95,
                      color: WHITE,
                      marginTop: 8,
                    }}
                  >
                    NO EMAIL!
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: RED,
                    color: WHITE,
                    padding: "12px 16px",
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 28,
                    textAlign: "center",
                    letterSpacing: "0.05em",
                  }}
                >
                  TIDAK BUTUH
                </div>

                <div style={{ fontSize: 18, color: "#8a9e94", lineHeight: 1.4 }}>
                  Gak ada palak email. Gak ada newsletter sampah. Langsung top up.
                </div>
              </div>
            </div>

            {/* BOTTOM BADGE */}
            <div
              style={{
                backgroundColor: LIME,
                color: BG,
                padding: "18px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 30,
                  letterSpacing: "0.06em",
                }}
              >
                CUKUP BUKA WEB &gt;&gt; MASUKIN ID &gt;&gt; SELESAI
              </span>
              <span style={{ fontFamily: "'AntonDisplay'", fontSize: 32 }}>100% CLEAN</span>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 10 [1260 - 1440]: INPUT CUMA ID GAME "84920194 (2026)" + BADGE      */}
      {/* ========================================================================= */}
      {isS10 && (() => {
        const spr = getSceneSpring(1260);
        const blink = Math.floor(frame / 10) % 2 === 0;

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            {/* CLEAN WEB UI HEADER */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "18px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: RED }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: LIME }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: WA_GREEN }} />
                <span style={{ marginLeft: 12, fontFamily: "monospace", fontSize: 18, color: "#8a9e94" }}>
                  kyustore.com/mobile-legends
                </span>
              </div>
              <span
                style={{
                  backgroundColor: LIME,
                  color: BG,
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 22,
                  padding: "4px 12px",
                }}
              >
                STEP 1: 1 BARIS DOANG
              </span>
            </div>

            {/* MAIN INPUT CONTAINER */}
            <div
              style={{
                backgroundColor: CARD,
                border: `3px solid ${LIME}`,
                padding: "36px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: 20,
                    color: LIME,
                    letterSpacing: "0.06em",
                  }}
                >
                  MASUKKAN ID GAME DAN SERVER:
                </span>
                {/* BADGE CUKUP INI */}
                <span
                  style={{
                    backgroundColor: LIME,
                    color: BG,
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 26,
                    padding: "6px 16px",
                  }}
                >
                  CUKUP INI ✓
                </span>
              </div>

              {/* INPUT BOX */}
              <div
                style={{
                  backgroundColor: BG,
                  border: `2px solid ${BORDER}`,
                  padding: "24px 28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 48,
                    color: WHITE,
                    letterSpacing: "0.08em",
                  }}
                >
                  84920194 (2026)
                </span>
                <span
                  style={{
                    width: 16,
                    height: 48,
                    backgroundColor: blink ? LIME : "transparent",
                  }}
                />
              </div>

              <div style={{ fontSize: 18, color: "#8a9e94", fontFamily: "monospace" }}>
                * Sistem mengenali akun tanpa perlu login password atau OTP.
              </div>
            </div>

            {/* ASYMMETRIC HIGHLIGHT */}
            <div
              style={{
                backgroundColor: CARD,
                border: `1px solid ${BORDER}`,
                padding: "24px 28px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 18, color: "#8a9e94", fontFamily: "monospace" }}>
                  TOTAL WAKTU INPUT:
                </div>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 54,
                    color: LIME,
                    marginTop: 4,
                  }}
                >
                  KURANG DARI 3 DETIK
                </div>
              </div>
              <div
                style={{
                  width: 56,
                  height: 56,
                  backgroundColor: LIME,
                  color: BG,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 34,
                  fontWeight: 900,
                }}
              >
                ✓
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 11 [1440 - 1620]: AUTO-DETECT "Zura_Official_ML" + "100% COCOK"     */}
      {/* ========================================================================= */}
      {isS11 && (() => {
        const spr = getSceneSpring(1440);
        const cardScale = interpolate(spr, [0, 1], [0.9, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 22,
              transform: `scale(${cardScale})`,
            }}
          >
            {/* ENGINE HEADER */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "18px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 30,
                  color: WHITE,
                  letterSpacing: "0.08em",
                }}
              >
                ENGINE VALIDASI SERVER KYU STORE
              </span>
              <span
                style={{
                  backgroundColor: WA_GREEN,
                  color: BG,
                  padding: "4px 12px",
                  fontWeight: "bold",
                  fontFamily: "monospace",
                }}
              >
                CONNECTED (12ms)
              </span>
            </div>

            {/* DETECTED PROFILE CARD */}
            <div
              style={{
                backgroundColor: CARD,
                border: `3px solid ${LIME}`,
                padding: "36px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 18, color: "#8a9e94", fontFamily: "monospace" }}>
                  NICKNAME DITEMUKAN:
                </span>
                {/* 100% COCOK BADGE */}
                <span
                  style={{
                    backgroundColor: LIME,
                    color: BG,
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 28,
                    padding: "6px 18px",
                  }}
                >
                  100% COCOK
                </span>
              </div>

              {/* NICKNAME BIG DISPLAY */}
              <div
                style={{
                  backgroundColor: BG,
                  border: `2px solid ${BORDER}`,
                  padding: "24px 28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 56,
                    color: WHITE,
                    letterSpacing: "0.04em",
                  }}
                >
                  Zura_Official_ML
                </span>
                <span
                  style={{
                    color: LIME,
                    fontSize: 40,
                    fontWeight: 900,
                  }}
                >
                  ✓
                </span>
              </div>

              {/* METADATA GRID */}
              <div style={{ display: "flex", gap: 16 }}>
                <div
                  style={{
                    flex: 1,
                    backgroundColor: BG,
                    padding: "16px 20px",
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <div style={{ fontSize: 14, color: "#8a9e94", fontFamily: "monospace" }}>
                    USER ID
                  </div>
                  <div style={{ fontSize: 24, fontWeight: "bold", color: WHITE, marginTop: 4 }}>
                    84920194
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    backgroundColor: BG,
                    padding: "16px 20px",
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <div style={{ fontSize: 14, color: "#8a9e94", fontFamily: "monospace" }}>
                    SERVER ZONE
                  </div>
                  <div style={{ fontSize: 24, fontWeight: "bold", color: WHITE, marginTop: 4 }}>
                    2026 (ID)
                  </div>
                </div>
              </div>
            </div>

            {/* STATUS CONFIRMATION */}
            <div
              style={{
                backgroundColor: CARD,
                border: `1px solid ${BORDER}`,
                padding: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 22, color: WHITE }}>
                Detik itu juga langsung terverifikasi otomatis.
              </span>
              <span
                style={{
                  fontFamily: "'BebasDisplay'",
                  fontSize: 24,
                  color: LIME,
                }}
              >
                AMAN & AKURAT
              </span>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 12 [1620 - 1800]: QRIS SCAN + SPEED BADGE "1.2 DETIK LANSUNG MASUK"  */}
      {/* ========================================================================= */}
      {isS12 && (() => {
        const spr = getSceneSpring(1620);
        // Laser scan line movement
        const laserY = interpolate(frame % 45, [0, 44], [0, 240], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 240,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              gap: 22,
            }}
          >
            {/* PAYMENT BAR */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "18px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 32,
                  color: WHITE,
                  letterSpacing: "0.08em",
                }}
              >
                METODE: QRIS INSTAN NASIONAL
              </span>
              <span
                style={{
                  backgroundColor: LIME,
                  color: BG,
                  padding: "4px 12px",
                  fontWeight: "bold",
                  fontFamily: "monospace",
                }}
              >
                SUPPORT ALL E-WALLET
              </span>
            </div>

            {/* QRIS MOCKUP & SCAN LASER */}
            <div
              style={{
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                padding: "28px 32px",
                display: "flex",
                gap: 28,
                alignItems: "center",
              }}
            >
              {/* QR BOX SIMULATION */}
              <div
                style={{
                  position: "relative",
                  width: 240,
                  height: 240,
                  backgroundColor: WHITE,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                {/* QR CORNER MARKS */}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={{ width: 48, height: 48, border: "10px solid #111815" }} />
                  <div style={{ width: 48, height: 48, border: "10px solid #111815" }} />
                </div>
                <div
                  style={{
                    alignSelf: "center",
                    fontFamily: "'AntonDisplay'",
                    fontSize: 26,
                    color: "#111815",
                  }}
                >
                  QRIS
                </div>
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div style={{ width: 48, height: 48, border: "10px solid #111815" }} />
                </div>

                {/* ANIMATED SCAN LASER LINE */}
                <div
                  style={{
                    position: "absolute",
                    top: laserY,
                    left: 0,
                    right: 0,
                    height: 4,
                    backgroundColor: RED,
                  }}
                />
              </div>

              {/* DETAILS COLUMN */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 16, color: "#8a9e94", fontFamily: "monospace" }}>
                  STATUS TRANSAKSI:
                </div>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 48,
                    color: LIME,
                    lineHeight: 1,
                  }}
                >
                  SCAN &amp; SELESAI
                </div>
                <div style={{ fontSize: 18, color: WHITE, lineHeight: 1.3 }}>
                  Satu klik dari aplikasi bank / e-wallet apa saja.
                </div>
              </div>
            </div>

            {/* SPEED BADGE */}
            <div
              style={{
                backgroundColor: LIME,
                color: BG,
                padding: "24px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: "bold" }}>
                SPEED BENCHMARK RESMI:
              </div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 64,
                  lineHeight: 0.9,
                  letterSpacing: "-0.01em",
                }}
              >
                1.2 DETIK LANSUNG MASUK
              </div>
              <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 700 }}>
                LANGSUNG MENDARAT DI AKUN GAME ANDA
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SCENE 13 [1800 - 2028]: OUTRO KYUSTORE.COM + LOGO                         */}
      {/* ========================================================================= */}
      {isS13 && (() => {
        const spr = getSceneSpring(1800);
        const outroScale = interpolate(spr, [0, 1], [0.92, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        return (
          <div
            style={{
              position: "absolute",
              top: 230,
              left: 90,
              right: 90,
              bottom: 540,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              transform: `scale(${outroScale})`,
              textAlign: "center",
            }}
          >
            {/* BRAND BADGE */}
            <div
              style={{
                padding: "10px 24px",
                backgroundColor: CARD,
                border: `2px solid ${BORDER}`,
                fontFamily: "'BebasDisplay', sans-serif",
                fontSize: 28,
                color: LIME,
                letterSpacing: "0.1em",
              }}
            >
              REKOMENDASI NOMOR 1 GAMER INDONESIA
            </div>

            {/* LOGO IMAGE */}
            <div
              style={{
                width: 220,
                height: 220,
                backgroundColor: CARD,
                border: `3px solid ${LIME}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                boxSizing: "border-box",
              }}
            >
              <Img
                src={staticFile("kyu-store-logo.png")}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>

            {/* DOMAIN BIG TYPE */}
            <div>
              <div
                style={{
                  fontFamily: "'AntonDisplay', sans-serif",
                  fontSize: 110,
                  lineHeight: 0.9,
                  color: WHITE,
                  letterSpacing: "-0.01em",
                }}
              >
                KYUSTORE.COM
              </div>
              <div
                style={{
                  fontFamily: "'BebasDisplay', sans-serif",
                  fontSize: 42,
                  color: LIME,
                  letterSpacing: "0.08em",
                  marginTop: 10,
                }}
              >
                TOP UP GAME ANTI-RIBET &amp; TERCEPAT
              </div>
            </div>

            {/* 3 PILL ADVANTAGES */}
            <div
              style={{
                display: "flex",
                gap: 16,
                width: "100%",
              }}
            >
              <div
                style={{
                  flex: 1,
                  backgroundColor: CARD,
                  border: `2px solid ${BORDER}`,
                  padding: "16px 8px",
                  fontSize: 20,
                  fontFamily: "'BebasDisplay', sans-serif",
                  color: WHITE,
                  letterSpacing: "0.06em",
                }}
              >
                NO WHATSAPP
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: CARD,
                  border: `2px solid ${BORDER}`,
                  padding: "16px 8px",
                  fontSize: 20,
                  fontFamily: "'BebasDisplay', sans-serif",
                  color: WHITE,
                  letterSpacing: "0.06em",
                }}
              >
                NO EMAIL
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: CARD,
                  border: `2px solid ${LIME}`,
                  padding: "16px 8px",
                  fontSize: 20,
                  fontFamily: "'BebasDisplay', sans-serif",
                  color: LIME,
                  letterSpacing: "0.06em",
                }}
              >
                1.2 DETIK MASUK
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* SUB JUDUL DINAMIS (HARD SPEC: top 1450, left 90, right 90, height 72)     */}
      {/* bg #18231f, border #283a33, teks 22px bold putih                         */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: 1450,
          left: 90,
          right: 90,
          height: 72,
          backgroundColor: CARD,
          border: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 24px",
          zIndex: 100,
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            fontSize: 22,
            fontWeight: "bold",
            color: WHITE,
            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
            textAlign: "center",
            lineHeight: 1.25,
            letterSpacing: "-0.01em",
            whiteSpace: "normal",
          }}
        >
          {currentSubtitle}
        </span>
      </div>

      {/* FOOTER BAR BRANDING (ACCENT) */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 16,
          zIndex: 90,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, backgroundColor: isS8 ? BG : LIME }} />
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 16,
              color: isS8 ? BG : "#8a9e94",
              letterSpacing: "0.06em",
            }}
          >
            KYU STORE OFFICIAL // ZERO BUREAUCRACY
          </span>
        </div>
        <span
          style={{
            fontFamily: "monospace",
            fontSize: 16,
            color: isS8 ? BG : LIME,
            fontWeight: "bold",
          }}
        >
          FRAME {frame} / 2028
        </span>
      </div>
    </AbsoluteFill>
  );
};
